import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, differenceInDays } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AccountSelector } from "@/components/plaid/account-selector";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useTransactions } from "@/hooks/use-transactions";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileUp, AlertCircle, CalendarDays, LinkIcon, Calendar, Download, Loader2, CheckCircle2 } from "lucide-react";

// Add Plaid type definition for TypeScript
declare global {
  interface Window {
    Plaid: any;
  }
}

// CSV Import schema
const csvImportSchema = z.object({
  file: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, "CSV file is required")
    .transform((files) => files[0]),
});

// Financial file import schema (QBO/OFX)
const financialFileSchema = z.object({
  file: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, "File is required")
    .refine(
      (files) => {
        const filename = files[0]?.name.toLowerCase() || '';
        return filename.endsWith('.qbo') || filename.endsWith('.ofx');
      },
      "File must be a .qbo or .ofx file"
    )
    .transform((files) => files[0]),
});

// Plaid Import schema
const plaidImportSchema = z.object({
  dateRange: z.enum(["custom", "current-year", "previous-year", "two-years-ago"]),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.dateRange === "custom" && (!data.startDate || !data.endDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Start and end dates are required for custom date range",
      path: ['startDate']
    });
  }
});

type CSVImportFormValues = z.infer<typeof csvImportSchema>;
type FinancialFileImportValues = z.infer<typeof financialFileSchema>;
type PlaidImportFormValues = z.infer<typeof plaidImportSchema>;

interface ImportTransactionsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ImportTransactions({
  open,
  onOpenChange,
  onSuccess,
}: ImportTransactionsProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("csv");
  const [isLinkingPlaid, setIsLinkingPlaid] = useState<boolean>(false);
  const [plaidError, setPlaidError] = useState<string | null>(null);
  const [financialFileError, setFinancialFileError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [showProgress, setShowProgress] = useState<boolean>(false);
  
  const {
    importCSV,
    importFinancialFile,
    createPlaidLinkToken,
    exchangePlaidToken,
    importPlaidTransactions,
    isPendingCSVImport,
    isPendingFinancialFileImport,
    isPendingCreatePlaidToken,
    isPendingExchangeToken,
    isPendingPlaidImport,
    effectiveBusinessId,
    isPlaidConnected,
    plaidConnectionDate,
    isLoadingPlaidStatus,
    refetchPlaidStatus
  } = useTransactions();
  
  // Get today and 30 days ago for default date range
  const today = format(new Date(), "yyyy-MM-dd");
  const thirtyDaysAgo = format(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    "yyyy-MM-dd"
  );
  
  // CSV import form
  const csvForm = useForm<CSVImportFormValues>({
    resolver: zodResolver(csvImportSchema),
  });
  
  // Financial file import form
  const financialFileForm = useForm<FinancialFileImportValues>({
    resolver: zodResolver(financialFileSchema),
  });
  
  // Plaid import form
  const plaidForm = useForm<PlaidImportFormValues>({
    resolver: zodResolver(plaidImportSchema),
    defaultValues: {
      dateRange: "current-year",
      startDate: "",
      endDate: "",
    },
  });
  
  // Calculate date ranges based on selection
  const getDateRange = (selection: string): { startDate: string, endDate: string } => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch(selection) {
      case "current-year":
        return {
          startDate: `${currentYear}-01-01`,
          endDate: format(now, "yyyy-MM-dd")
        };
      case "previous-year":
        return {
          startDate: `${currentYear - 1}-01-01`,
          endDate: `${currentYear - 1}-12-31`
        };
      case "two-years-ago":
        return {
          startDate: `${currentYear - 2}-01-01`,
          endDate: `${currentYear - 2}-12-31`
        };
      default:
        return {
          startDate: plaidForm.getValues("startDate") || "",
          endDate: plaidForm.getValues("endDate") || ""
        };
    }
  };

  // Handle Financial file import (QBO/OFX)
  const onSubmitFinancialFile = async (data: FinancialFileImportValues) => {
    try {
      setFinancialFileError(null);
      const formData = new FormData();
      formData.append("file", data.file);
      
      // Check file extension to ensure it's valid
      const fileName = data.file.name.toLowerCase();
      if (!fileName.endsWith('.qbo') && !fileName.endsWith('.ofx')) {
        toast({
          title: "Invalid File Format",
          description: "Please select a .qbo or .ofx file exported from your financial institution or accounting software.",
          variant: "destructive",
        });
        return;
      }
      
      // Check file size
      if (data.file.size > 5 * 1024 * 1024) {
        toast({
          title: "Warning",
          description: "The selected file is quite large. Processing may take longer than expected.",
          variant: "destructive",
        });
      }
      
      // Show progress bar
      setShowProgress(true);
      setImportProgress(10); // Initial progress
      
      // Simulate progress during file processing
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => {
          // Don't go beyond 90% until we get the actual response
          return prev < 90 ? prev + 10 : prev;
        });
      }, 500);
      
      try {
        const response = await importFinancialFile(formData);
        
        // When response is received, set to 100%
        clearInterval(progressInterval);
        setImportProgress(100);
        
        // Check if response is OK
        if (!response.ok) {
          const errorData = await response.json();
          
          // Handle trial limit errors specifically
          if (errorData.error === "TRIAL_LIMIT_REACHED") {
            setFinancialFileError(errorData.message || "You've reached your trial limit for transactions. Please upgrade your plan to continue.");
            
            toast({
              title: "Trial Limit Reached",
              description: errorData.message || "You've reached your trial limit for transactions. Please upgrade your plan to continue.",
              variant: "destructive",
            });
            return;
          } else if (errorData.error === "PARTIAL_TRIAL_LIMIT") {
            const errorMessage = errorData.message || `You can only import ${errorData.remaining} more transactions with your trial.`;
            setFinancialFileError(errorMessage);
            
            toast({
              title: "Trial Limit Warning",
              description: errorMessage,
              variant: "destructive",
            });
            
            // If it's a partial limit, we might still have some data to work with
            if (!errorData.data || !errorData.data.count) {
              // If no data was processed, return early
              return;
            }
          } else {
            // Handle other errors
            const errorMessage = errorData.message || "Failed to import financial file";
            setFinancialFileError(errorMessage);
            throw new Error(errorMessage);
          }
        }
        
        // Parse the successful response
        const result = await response.json();
        financialFileForm.reset();
        
        // Show success message
        toast({
          title: "Transactions Imported",
          description: `Successfully imported ${result.count || 0} transactions.`,
        });
        
        // If trial usage information is provided, show it
        if (result.trialUsage) {
          const { used, limit, remaining } = result.trialUsage;
          toast({
            title: "Trial Usage Updated",
            description: `You've used ${used}/${limit} transactions (${remaining} remaining).`,
            variant: "default",
          });
        }
        
        if (onSuccess) {
          onSuccess();
        }
        
        // Hide progress bar after success
        setTimeout(() => {
          setShowProgress(false);
          setImportProgress(0);
        }, 1000);
        
        onOpenChange(false);
      } catch (error: any) {
        // Handle errors and clear progress bar
        clearInterval(progressInterval);
        setShowProgress(false);
        setImportProgress(0);
        throw error;
      }
    } catch (error: any) {
      console.error("Failed to import financial file:", error);
      
      // Extract detailed error message if available
      let errorMessage = "Failed to import financial file";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Add helpful tips based on error type
      if (error.message?.includes("Invalid file format") || error.message?.includes("Failed to parse")) {
        errorMessage += ". Make sure you're uploading a valid QBO or OFX file exported from your bank or financial software.";
      }
      
      setFinancialFileError(errorMessage);
      
      toast({
        title: "Import Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Handle CSV import
  const onSubmitCSV = async (data: CSVImportFormValues) => {
    try {
      const formData = new FormData();
      formData.append("file", data.file);
      
      // Check the file type to provide early warnings
      if (data.file.type && !['text/csv', 'application/vnd.ms-excel', 'text/plain'].includes(data.file.type)) {
        toast({
          title: "Warning",
          description: "The selected file may not be a CSV file. Make sure your file is in CSV format.",
          variant: "destructive",
        });
      }
      
      // Check file size
      if (data.file.size > 2 * 1024 * 1024) {
        toast({
          title: "Warning",
          description: "The selected file is quite large. Processing may take longer than expected.",
          variant: "destructive",
        });
      }
      
      // Show progress bar
      setShowProgress(true);
      setImportProgress(10); // Initial progress
      
      // Simulate progress during file processing
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => {
          // Don't go beyond 90% until we get the actual response
          return prev < 90 ? prev + 10 : prev;
        });
      }, 500);
      
      try {
        const response = await importCSV(formData);
        
        // When response is received, set to 100%
        clearInterval(progressInterval);
        setImportProgress(100);
        
        // Check if response is OK
        if (!response.ok) {
          const errorData = await response.json();
          
          // Handle trial limit errors specifically
          if (errorData.error === "TRIAL_LIMIT_REACHED") {
            toast({
              title: "Trial Limit Reached",
              description: errorData.message || "You've reached your trial limit for transactions. Please upgrade your plan to continue.",
              variant: "destructive",
            });
            return;
          } else if (errorData.error === "PARTIAL_TRIAL_LIMIT") {
            toast({
              title: "Trial Limit Warning",
              description: errorData.message || `You can only import ${errorData.remaining} more transactions with your trial.`,
              variant: "destructive",
            });
            
            // If it's a partial limit, we might still have some data to work with
            if (!errorData.data || !errorData.data.count) {
              // If no data was processed, return early
              return;
            }
          } else {
            // Handle other errors
            throw new Error(errorData.message || "Failed to import CSV");
          }
        }
        
        // Parse the successful response
        const result = await response.json();
        csvForm.reset();
        
        // Show success message
        toast({
          title: "Transactions Imported",
          description: `Successfully imported ${result.count || 0} transactions.`,
        });
        
        // If trial usage information is provided, show it
        if (result.trialUsage) {
          const { used, limit, remaining } = result.trialUsage;
          toast({
            title: "Trial Usage Updated",
            description: `You've used ${used}/${limit} transactions (${remaining} remaining).`,
            variant: "default",
          });
        }
        
        if (onSuccess) {
          onSuccess();
        }
        
        // Hide progress bar after success
        setTimeout(() => {
          setShowProgress(false);
          setImportProgress(0);
        }, 1000);
        
        onOpenChange(false);
      } catch (error) {
        // Handle errors and clear progress bar
        clearInterval(progressInterval);
        setShowProgress(false);
        setImportProgress(0);
        throw error;
      }
    } catch (error: any) {
      console.error("Failed to import CSV:", error);
      
      // Extract detailed error message if available
      let errorMessage = "Failed to import CSV file";
      
      if (error.response) {
        try {
          const errorData = await error.response.json();
          errorMessage = errorData.message || errorData.details || errorMessage;
          
          // Add line information if available
          if (errorData.line) {
            errorMessage += ` (Line ${errorData.line})`;
          }
        } catch (e) {
          // If parsing JSON fails, use status text
          errorMessage = error.response.statusText || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Add helpful tips based on error message content
      if (errorMessage.includes("Invalid CSV format") || errorMessage.includes("Missing required columns")) {
        errorMessage += ". Please check that your CSV contains properly formatted date, description, and amount columns.";
      } else if (errorMessage.includes("No records found") || errorMessage.includes("No valid records")) {
        errorMessage += ". The uploaded file appears to be empty or missing required data.";
      } else if (errorMessage.includes("Failed to parse")) {
        errorMessage += ". Your file may have formatting issues. Try re-exporting it from your source program.";
      } else if (errorMessage.includes("columns length")) {
        errorMessage += ". Your file has inconsistent column counts. Make sure your CSV is properly formatted with consistent columns.";
      }
      
      toast({
        title: "Import Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };
  
  // Handle Plaid import
  const onSubmitPlaid = async (data: PlaidImportFormValues) => {
    try {
      // Clear any previous errors
      setPlaidError("");
      
      // Get the appropriate date range based on selection
      const { startDate, endDate } = data.dateRange === "custom" 
        ? { startDate: data.startDate!, endDate: data.endDate! }
        : getDateRange(data.dateRange);
      
      // Add validation for date range
      if (!startDate || !endDate) {
        setPlaidError("Please select a valid date range.");
        return;
      }
      
      // Add validation to ensure end date is not before start date
      if (new Date(endDate) < new Date(startDate)) {
        setPlaidError("End date cannot be before start date.");
        return;
      }
      
      // Calculate date range span and validate it's not too large
      const daysDiff = differenceInDays(new Date(endDate), new Date(startDate));
      if (daysDiff > 365) {
        setPlaidError("Date range cannot exceed 365 days. Please select a shorter period.");
        return;
      }
      
      // Show progress bar
      setShowProgress(true);
      setImportProgress(10); // Initial progress
      
      // Simulate progress during processing
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => {
          // Don't go beyond 90% until we get the actual response
          return prev < 90 ? prev + 10 : prev;
        });
      }, 500);
      
      try {
        const response = await importPlaidTransactions({ startDate, endDate });
        
        // When response is received, set to 100%
        clearInterval(progressInterval);
        setImportProgress(100);
        
        // Check if response is OK
        if (!response.ok) {
          const errorData = await response.json();
          
          // Handle trial limit errors specifically
          if (errorData.error === "TRIAL_LIMIT_REACHED") {
            const errorMessage = errorData.message || "You've reached your trial limit for transactions. Please upgrade your plan to continue.";
            setPlaidError(errorMessage);
            
            toast({
              title: "Trial Limit Reached",
              description: errorMessage,
              variant: "destructive",
            });
            return;
          } else if (errorData.error === "PARTIAL_TRIAL_LIMIT") {
            const errorMessage = errorData.message || `You can only import ${errorData.remaining} more transactions with your trial.`;
            setPlaidError(errorMessage);
            
            toast({
              title: "Trial Limit Warning",
              description: errorMessage,
              variant: "destructive",
            });
            
            // If it's a partial limit, we might still have some data to work with
            if (!errorData.data || !errorData.data.count) {
              // If no data was processed, return early
              return;
            }
          } else {
            // Handle other errors
            const errorMessage = errorData.message || "Failed to import transactions from Plaid";
            setPlaidError(errorMessage);
            throw new Error(errorMessage);
          }
        }
        
        // Parse the successful response
        const result = await response.json();
        
        // Show success message
        toast({
          title: "Transactions Imported",
          description: `Successfully imported ${result.count || 0} transactions.`,
        });
        
        // If trial usage information is provided, show it
        if (result.trialUsage) {
          const { used, limit, remaining } = result.trialUsage;
          toast({
            title: "Trial Usage Updated",
            description: `You've used ${used}/${limit} transactions (${remaining} remaining).`,
            variant: "default",
          });
        }
        
        if (onSuccess) {
          onSuccess();
        }
        
        // Hide progress bar after success
        setTimeout(() => {
          setShowProgress(false);
          setImportProgress(0);
        }, 1000);
        
        onOpenChange(false);
      } catch (error) {
        // Handle errors and clear progress bar
        clearInterval(progressInterval);
        setShowProgress(false);
        setImportProgress(0);
        throw error;
      }
    } catch (error: any) {
      console.error("Failed to import Plaid transactions:", error);
      
      // Extract detailed error message if available
      let errorMessage = "Failed to import transactions from Plaid";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      setPlaidError(errorMessage);
      
      toast({
        title: "Import Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };
  
  // Initialize Plaid connection
  const initPlaidConnection = async () => {
    try {
      setIsLinkingPlaid(true);
      setPlaidError("");
      
      const response = await createPlaidLinkToken();
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create Plaid link token");
      }
      
      const { link_token } = await response.json();
      
      if (!link_token) {
        throw new Error("No link token received from server");
      }
      
      // Initialize Plaid Link
      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token: string) => {
          try {
            // Exchange public token for access token
            const exchangeResponse = await exchangePlaidToken(public_token);
            
            if (!exchangeResponse.ok) {
              const errorData = await exchangeResponse.json();
              throw new Error(errorData.message || "Failed to exchange Plaid token");
            }
            
            toast({
              title: "Bank Connected",
              description: "Your bank account has been successfully connected. You can now import transactions.",
            });
            
            // Update Plaid connection status
            await refetchPlaidStatus();
            setIsLinkingPlaid(false);
          } catch (error: any) {
            console.error("Failed to exchange Plaid token:", error);
            setPlaidError(error.message || "Failed to complete bank connection");
            setIsLinkingPlaid(false);
          }
        },
        onExit: () => {
          setIsLinkingPlaid(false);
        },
        onLoad: () => {
          // Handler has loaded
        },
        onEvent: (eventName: string) => {
          // Handle Plaid Link events
          console.log("Plaid event:", eventName);
        },
      });
      
      // Open Plaid Link
      handler.open();
    } catch (error: any) {
      console.error("Failed to initialize Plaid:", error);
      setPlaidError(error.message || "Failed to initialize bank connection");
      setIsLinkingPlaid(false);
    }
  };
  
  // Reset forms when dialog closes
  const handleClose = () => {
    setPlaidError(null);
    setFinancialFileError(null);
    setShowProgress(false);
    setImportProgress(0);
    csvForm.reset();
    financialFileForm.reset();
    plaidForm.reset({
      dateRange: "current-year",
      startDate: "",
      endDate: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) handleClose();
      onOpenChange(value);
    }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import Transactions</DialogTitle>
          <DialogDescription>
            Import transactions from CSV files, bank exports, or connect to your bank account.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="csv" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="csv">CSV Import</TabsTrigger>
            <TabsTrigger value="financial-file">QBO/OFX Import</TabsTrigger>
            <TabsTrigger value="plaid">Bank Connection</TabsTrigger>
          </TabsList>
          
          {/* Show progress bar if active */}
          {showProgress && (
            <div className="my-4">
              <div className="flex justify-between mb-2">
                <span className="text-sm">Importing transactions...</span>
                <span className="text-sm">{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}
          
          {/* CSV Import Tab */}
          <TabsContent value="csv">
            <div className="space-y-4 py-4">
              <div className="flex items-start space-x-4">
                <div className="bg-muted p-3 rounded-md">
                  <FileUp className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">CSV File Upload</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV file with your transaction data.
                    The file should include columns for date, description, amount, and optionally category and account.
                  </p>
                </div>
              </div>
              
              <Form {...csvForm}>
                <form onSubmit={csvForm.handleSubmit(onSubmitCSV)} className="space-y-4">
                  <FormField
                    control={csvForm.control}
                    name="file"
                    render={({ field: { onChange, ...rest } }) => (
                      <FormItem>
                        <FormLabel>CSV File</FormLabel>
                        <FormControl>
                          <Input
                            type="file"
                            accept=".csv,text/csv,application/vnd.ms-excel"
                            onChange={(e) => {
                              onChange(e.target.files);
                            }}
                            disabled={isPendingCSVImport || showProgress}
                            // Removing {...rest} to prevent React from setting value on file input
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isPendingCSVImport || showProgress}>
                      {isPendingCSVImport || showProgress ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        "Import Transactions"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
              
              <div className="pt-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>CSV Format Tips</AlertTitle>
                  <AlertDescription>
                    <p className="text-sm">
                      Make sure your CSV file has these columns: Date, Description, Amount.
                      Optional columns: Category, Account, Status, Reference.
                    </p>
                    <p className="text-sm pt-2">
                      Dates should be in YYYY-MM-DD format (e.g., 2025-01-15).
                      Negative amounts (-100.00) are expenses, positive amounts are income.
                    </p>
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </TabsContent>
          
          {/* Financial File Import Tab (QBO/OFX) */}
          <TabsContent value="financial-file">
            <div className="space-y-4 py-4">
              <div className="flex items-start space-x-4">
                <div className="bg-muted p-3 rounded-md">
                  <Download className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Bank Export Upload</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload a financial file exported from your bank or accounting software.
                    Supported formats: QuickBooks (.qbo) and Open Financial Exchange (.ofx).
                  </p>
                </div>
              </div>
              
              {financialFileError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Import Error</AlertTitle>
                  <AlertDescription>{financialFileError}</AlertDescription>
                </Alert>
              )}
              
              <Form {...financialFileForm}>
                <form onSubmit={financialFileForm.handleSubmit(onSubmitFinancialFile)} className="space-y-4">
                  <FormField
                    control={financialFileForm.control}
                    name="file"
                    render={({ field: { onChange, ...rest } }) => (
                      <FormItem>
                        <FormLabel>Financial File</FormLabel>
                        <FormControl>
                          <Input
                            type="file"
                            accept=".qbo,.ofx"
                            onChange={(e) => {
                              onChange(e.target.files);
                            }}
                            disabled={isPendingFinancialFileImport || showProgress}
                            // Removing {...rest} to prevent React from setting value on file input
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isPendingFinancialFileImport || showProgress}>
                      {isPendingFinancialFileImport || showProgress ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        "Import Transactions"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
              
              <div className="pt-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>How to Export QBO/OFX Files</AlertTitle>
                  <AlertDescription>
                    <p className="text-sm">
                      Most online banking portals allow you to export transaction data in QBO or OFX format.
                      Look for "Export Transactions" or "Download Statement" options in your bank's website.
                    </p>
                    <p className="text-sm pt-2">
                      QBO files are exported from QuickBooks or most major banks.
                      OFX files are a common format supported by many financial institutions.
                    </p>
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </TabsContent>
          
          {/* Plaid Connection Tab */}
          <TabsContent value="plaid">
            <div className="space-y-4 py-4">
              <div className="flex items-start space-x-4">
                <div className="bg-muted p-3 rounded-md">
                  <LinkIcon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Bank Connection</h4>
                  <p className="text-sm text-muted-foreground">
                    Connect your bank account securely with Plaid to automatically import transactions.
                    Your credentials are never stored on our servers.
                  </p>
                </div>
              </div>
              
              {plaidError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Connection Error</AlertTitle>
                  <AlertDescription>{plaidError}</AlertDescription>
                </Alert>
              )}
              
              {isPlaidConnected ? (
                <>
                  <div className="bg-muted p-4 rounded-md mb-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="font-medium">Bank Connected</span>
                    </div>
                    {plaidConnectionDate && (
                      <div className="mt-2 flex items-center text-sm text-muted-foreground">
                        <CalendarDays className="mr-1 h-4 w-4" />
                        <span>Connected on {format(new Date(plaidConnectionDate), "MMM d, yyyy")}</span>
                      </div>
                    )}
                  </div>

                  {/* Account selection — lets user choose which accounts to import from */}
                  {effectiveBusinessId && <AccountSelector businessId={effectiveBusinessId} />}

                  <Form {...plaidForm}>
                    <form onSubmit={plaidForm.handleSubmit(onSubmitPlaid)} className="space-y-4">
                      <FormField
                        control={plaidForm.control}
                        name="dateRange"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date Range</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={isPendingPlaidImport || showProgress}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a date range" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="current-year">Current Year</SelectItem>
                                <SelectItem value="previous-year">Previous Year</SelectItem>
                                <SelectItem value="two-years-ago">Two Years Ago</SelectItem>
                                <SelectItem value="custom">Custom Range</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      {plaidForm.watch("dateRange") === "custom" && (
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={plaidForm.control}
                            name="startDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Start Date</FormLabel>
                                <FormControl>
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <Input
                                      type="date"
                                      {...field}
                                      disabled={isPendingPlaidImport || showProgress}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={plaidForm.control}
                            name="endDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>End Date</FormLabel>
                                <FormControl>
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <Input
                                      type="date"
                                      {...field}
                                      max={today}
                                      disabled={isPendingPlaidImport || showProgress}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onOpenChange(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" disabled={isPendingPlaidImport || showProgress}>
                          {isPendingPlaidImport || showProgress ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            "Import Transactions"
                          )}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="mb-4 text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      Connect your bank account to automatically import transactions.
                    </p>
                    <Button
                      onClick={initPlaidConnection}
                      disabled={isPendingCreatePlaidToken || isLinkingPlaid || isLoadingPlaidStatus}
                    >
                      {(isPendingCreatePlaidToken || isLinkingPlaid || isLoadingPlaidStatus) ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        "Connect Bank Account"
                      )}
                    </Button>
                  </div>
                  
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Secure Connection</AlertTitle>
                    <AlertDescription>
                      <p className="text-sm">
                        We use Plaid to securely connect to your bank. Your credentials are never stored on our servers.
                        Plaid is used by thousands of financial applications to securely connect bank accounts.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}